import { useState, useRef, useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { Command, CommandGroup, CommandItem } from "@/components/ui/command";
import { supabase } from "@/lib/supabase";

interface InlineGroupInputProps {
  value: string;
  onChange: (value: string) => void;
  workspaceId: string;
}

export function InlineGroupInput({ value, onChange, workspaceId }: InlineGroupInputProps) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async (input: string) => {
    if (!workspaceId || !input.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const { data, error } = await supabase
      .from('groups')
      .select('group_name')
      .eq('workspace_id', workspaceId)
      .ilike('group_name', `%${input}%`)
      .limit(5);

    if (error) {
      setSuggestions([]);
      return;
    }

    const names = data.map((g) => g.group_name);
    setSuggestions(names);
    setShowSuggestions(names.length > 0);
  };

  const handleChange = (input: string) => {
    onChange(input);
    if (input.trim()) {
      fetchSuggestions(input);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelect = (suggestion: string) => {
    onChange(suggestion);
    setShowSuggestions(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <Input
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => value.trim() && fetchSuggestions(value)}
        placeholder="Group name"
        className="h-7 text-xs"
      />

      {showSuggestions && suggestions.length > 0 && (
        <Command className="absolute z-50 w-full mt-1 border rounded-md shadow-md">
          <CommandGroup>
            {suggestions.map((suggestion) => (
              <CommandItem
                key={suggestion}
                onSelect={() => handleSelect(suggestion)}
                className="text-xs"
              >
                {suggestion}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      )}
    </div>
  );
}
